import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileJson, ClipboardPaste, Package, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { newKey, sendPackage, startJson } from "@/lib/imports";
import { Textarea } from "@/components/ui/textarea";

function MethodCard({ icon: Icon, title, children, testid }) {
  return (
    <div className="panel flex flex-col gap-4 p-6" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-lg border border-amber-700/50 bg-amber-950/30"><Icon className="h-5 w-5 text-amber-300" /></span>
        <h2 className="font-heading text-lg text-amber-100">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function useStart() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      const draft = await fn();
      toast.success(`${draft.summary.total} card${draft.summary.total === 1 ? "" : "s"} read`);
      navigate(`/import/${draft.draft_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

export function JsonFileCard() {
  const [busy, run] = useStart();
  const pick = (file) => file && run(async () => startJson(await file.text(), "file", file.name));
  return (
    <MethodCard icon={FileJson} title="Upload a JSON file" testid="import-json-card">
      <p className="text-sm text-slate-400">One card or many, in the Planar Foundry import format. Add images on the next screen.</p>
      <label className="btn-gold mt-auto cursor-pointer" data-testid="import-json-label">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />}Choose .json file
        <input type="file" accept=".json,application/json" className="hidden" disabled={busy} data-testid="import-json-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pick(f); }} />
      </label>
    </MethodCard>
  );
}

export function PasteCard() {
  const [busy, run] = useStart();
  const [text, setText] = useState("");
  return (
    <MethodCard icon={ClipboardPaste} title="Paste import code" testid="import-paste-card">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder='{"schema": "planar-foundry.import", "version": 1, "cards": [...]}'
        className="field font-mono text-xs" data-testid="import-paste-textarea" />
      <button className="btn-gold" disabled={busy || text.trim().length < 2} onClick={() => run(() => startJson(text, "paste"))} data-testid="import-paste-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}Read import code
      </button>
    </MethodCard>
  );
}

export function PackageCard() {
  const [busy, run] = useStart();
  const [progress, setProgress] = useState(null);
  const pick = (file) => file && run(async () => {
    try {
      const uploadId = await sendPackage(file, (done, total) => setProgress({ done, total }));
      return (await api.post("/imports/package", { upload_id: uploadId, request_key: newKey() })).data;
    } finally {
      setProgress(null);
    }
  });
  return (
    <MethodCard icon={Package} title="Upload a set package" testid="import-package-card">
      <p className="text-sm text-slate-400">A .zip (up to 500 MB) with a manifest.json and the finished card images. Images are matched for you.</p>
      {progress && (
        <div className="space-y-1" data-testid="import-package-progress">
          <p className="font-mono text-xs text-slate-400">Uploading {Math.round((progress.done / progress.total) * 100)}%</p>
          <div className="h-1.5 overflow-hidden rounded-full bg-[#2A3447]"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
        </div>
      )}
      <label className="btn-gold mt-auto cursor-pointer" data-testid="import-package-label">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}Choose .zip package
        <input type="file" accept=".zip,application/zip" className="hidden" disabled={busy} data-testid="import-package-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pick(f); }} />
      </label>
    </MethodCard>
  );
}
